import { FilterToken, SearchFilterState, normalizeColors } from 'screens/Main/Search/searchState';
import { COLOR_PIPS } from './CardFilterTokenRow';

/**
 * Human-readable rendering of the active filters for the filtered-to-zero
 * state. Tokens (deriveTokens) carry compact query-style labels like
 * "name: tetsuos" or "mv = 1"; the empty state speaks in prose instead:
 * chip "Mana value = 1", clause "with a mana value of 1", button "Clear mana value".
 */
export interface HumanFilter {
  /** Stable id: the token id, or `color:<key>` for one color of the colors token. */
  id: string;
  /** Chip text, e.g. "Blue", "Mana value = 1", "Name: tetsuos". */
  chip: string;
  /** Short name for "Clear <name>", e.g. "mana value", "name", "colors". */
  shortName: string;
  /** Mana color key when the chip is one color (tinted chip + glyph). */
  colorKey?: string;
  /** Set code / name when the chip is the set filter (glyph row). */
  setLabel?: string;
  /** Pure removal, same contract as FilterToken.clear. */
  clear: (state: SearchFilterState) => SearchFilterState;
}

const COMPARISON_WORD: Record<string, string> = {
  eq: '=',
  gt: '>',
  gte: '≥',
  lt: '<',
  lte: '≤',
};

const COMPARISON_PHRASE: Record<string, string> = {
  eq: 'of',
  gt: 'over',
  gte: 'of at least',
  lt: 'under',
  lte: 'of at most',
};

const quote = (s: string) => `"${s.trim()}"`;

/** Chips: one per color, then one per other token, in token order. */
export const humanizeFilters = (state: SearchFilterState, tokens: FilterToken[]): HumanFilter[] => {
  const out: HumanFilter[] = [];
  for (const token of tokens) {
    if (token.id === 'colors') {
      for (const key of normalizeColors(state.colors)) {
        out.push({
          id: `color:${key}`,
          chip: COLOR_PIPS[key]?.label ?? key,
          shortName: COLOR_PIPS[key]?.label.toLowerCase() ?? key,
          colorKey: key,
          clear: (s) => {
            const colors = s.colors.filter((c) => c !== key);
            return { ...s, colors, colorsMode: colors.length ? s.colorsMode : 'including' };
          },
        });
      }
      continue;
    }
    out.push({
      id: token.id,
      chip: chipFor(token, state),
      shortName: shortNameFor(token.id),
      setLabel: token.id === 'setName' ? state.setName.trim() : undefined,
      clear: token.clear,
    });
  }
  return out;
};

const chipFor = (token: FilterToken, s: SearchFilterState): string => {
  switch (token.id) {
    case 'name': return `Name: ${s.name.trim()}`;
    case 'oracleText': return `Rules text: ${s.oracleText.trim()}`;
    case 'typeLine': return `Type: ${s.typeLine.trim()}`;
    case 'setName': return `Set: ${s.setName.trim()}`;
    case 'flavorText': return `Flavor: ${s.flavorText.trim()}`;
    case 'artist': return `Artist: ${s.artist.trim()}`;
    case 'manaCost': return `Mana cost: ${s.manaCost.trim()}`;
    case 'cmc': return `Mana value ${COMPARISON_WORD[s.cmcComparison]} ${s.cmc}`;
    case 'power': return `Power ${COMPARISON_WORD[s.powerComparison]} ${s.power}`;
    case 'toughness': return `Toughness ${COMPARISON_WORD[s.toughnessComparison]} ${s.toughness}`;
    case 'points': return pointsChip(s);
    case 'availableToSend': return 'Available to send';
    case 'availableToReceive': return 'Available to receive';
    case 'format': return `${s.format} (${s.formatLegality === 'legal' ? 'legal' : 'not legal'})`;
    case 'lang': return `Language: ${s.lang}`;
    case 'reserved': return 'Reserved list';
    default: return token.label;
  }
};

const pointsChip = (s: SearchFilterState): string => {
  const hasMin = s.pointsMin !== '' && Number(s.pointsMin) > 0;
  const hasMax = s.pointsMax !== '';
  if (hasMin && hasMax) return `${s.pointsMin}–${s.pointsMax} points`;
  if (hasMin) return `Over ${s.pointsMin} points`;
  return `Under ${s.pointsMax} points`;
};

const SHORT_NAMES: Record<string, string> = {
  name: 'name',
  oracleText: 'rules text',
  typeLine: 'type',
  setName: 'set',
  flavorText: 'flavor text',
  artist: 'artist',
  manaCost: 'mana cost',
  cmc: 'mana value',
  power: 'power',
  toughness: 'toughness',
  points: 'point value',
  availableToSend: 'available to send',
  availableToReceive: 'available to receive',
  format: 'format',
  lang: 'language',
  reserved: 'reserved list',
};
const shortNameFor = (id: string): string => SHORT_NAMES[id] ?? id;

const joinNatural = (parts: string[], word: 'or' | 'and'): string => {
  if (parts.length <= 1) return parts.join('');
  return `${parts.slice(0, -1).join(', ')} ${word} ${parts[parts.length - 1]}`;
};

/**
 * The clause after "none of them": prose, not query syntax.
 *   colors → "are Blue or Green" / "are exactly Blue and Green" / "are at most Blue or Green"
 *   name   → 'match "tetsuos"'
 *   cmc    → "have a mana value of 1"  (or "with a mana value of 1" after a color clause)
 * Clauses join naturally: the first carries its verb, the rest attach with "with"/"and".
 */
export const describeFilters = (state: SearchFilterState, tokens: FilterToken[]): string => {
  const colors = normalizeColors(state.colors).map((c) => COLOR_PIPS[c]?.label ?? c);
  const clauses: string[] = [];
  const attachments: string[] = [];

  if (colors.length > 0) {
    const list = joinNatural(colors, state.colorsMode === 'exactly' ? 'and' : 'or');
    const prefix = state.colorsMode === 'exactly' ? 'exactly ' : state.colorsMode === 'at_most' ? 'at most ' : '';
    clauses.push(`are ${prefix}${list}`);
  }

  for (const token of tokens) {
    switch (token.id) {
      case 'colors':
        break;
      case 'name':
        clauses.push(`match ${quote(state.name)}`);
        break;
      case 'cmc':
        attachments.push(`a mana value ${COMPARISON_PHRASE[state.cmcComparison]} ${state.cmc}`);
        break;
      case 'power':
        attachments.push(`power ${COMPARISON_PHRASE[state.powerComparison]} ${state.power}`);
        break;
      case 'toughness':
        attachments.push(`toughness ${COMPARISON_PHRASE[state.toughnessComparison]} ${state.toughness}`);
        break;
      case 'typeLine':
        attachments.push(`${quote(state.typeLine)} in the type line`);
        break;
      case 'oracleText':
        attachments.push(`${quote(state.oracleText)} in the rules text`);
        break;
      case 'setName':
        attachments.push(`a set matching ${quote(state.setName)}`);
        break;
      case 'manaCost':
        attachments.push(`a mana cost of ${state.manaCost.trim()}`);
        break;
      case 'flavorText':
        attachments.push(`${quote(state.flavorText)} in the flavor text`);
        break;
      case 'artist':
        attachments.push(`art by ${state.artist.trim()}`);
        break;
      case 'points':
        attachments.push(pointsChip(state).toLowerCase().replace(/^over/, 'a value over').replace(/^under/, 'a value under'));
        break;
      case 'availableToSend':
        clauses.push('are available to send');
        break;
      case 'availableToReceive':
        clauses.push('are available to receive');
        break;
      case 'format':
        clauses.push(`are ${state.formatLegality === 'legal' ? 'legal' : 'not legal'} in ${state.format}`);
        break;
      case 'lang':
        clauses.push(`are in ${state.lang}`);
        break;
      case 'reserved':
        clauses.push('are on the reserved list');
        break;
      default:
        attachments.push(token.label);
    }
  }

  const head = clauses.length > 0 ? joinNatural(clauses, 'and') : '';
  if (attachments.length === 0) return head;
  const tail = joinNatural(attachments, 'and');
  return head ? `${head} with ${tail}` : `have ${tail}`;
};
